import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Card, CardContent, Grid, Typography, TextField,
    Button, Checkbox, FormControlLabel, makeStyles
} from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import TreeView from '@material-ui/lab/TreeView';
import TreeItem from '@material-ui/lab/TreeItem';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import Header from "../Layouts/Header";
import Footer from "../Layouts/Footer";
import Breadcrumb from "../Layouts/Breadcrumb";
import AuthApi from '../Services/Authapi';
import swal from "sweetalert";
import AuthCheckComponent from '../Layouts/Token';
import TmsUserSelect from './TmsUserSelect';
import './Leaves/Leave.css';

const useStyles = makeStyles((theme) => ({
    treeItem: {
        display: 'flex',
        alignItems: 'center',
        paddingLeft: theme.spacing(1), // Adjust as needed
    },
    tmsTreeItemContent: {
        alignItems: 'flex-start !important',
        '& > .MuiTreeItem-iconContainer': {
            marginTop: '8px !important',
        },
    },
    tmsTreeItemIcon: {
        marginTop: '8px !important',
    },
}));

const createLoaderContent = () => {
    const loaderContainer = document.createElement('div');
    loaderContainer.style.display = 'flex';
    loaderContainer.style.justifyContent = 'center';
    loaderContainer.style.alignItems = 'center';
    loaderContainer.style.marginTop = '12px';
    loaderContainer.innerHTML = `
      <svg width="42" height="42" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg" aria-label="Loading">
        <circle cx="25" cy="25" r="20" fill="none" stroke="#d9ebff" stroke-width="5"></circle>
        <circle cx="25" cy="25" r="20" fill="none" stroke="var(--hrms-primary-blue-end, #036dda)" stroke-width="5" stroke-linecap="round" stroke-dasharray="31.4 125.6">
          <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="0.9s" values="0 25 25;360 25 25"></animateTransform>
        </circle>
      </svg>
    `;
    return loaderContainer;
};

const EditRole = (props) => {
    const history = useHistory();
    const classes = useStyles();
    const [editValues, setEditValues] = useState({
        roleName: "",
        event_Id: "",
        permissions: [],
        tms_user_ids: [],
    });
    const [permissions, setPermissions] = useState({});
    const [permissionSearch, setPermissionSearch] = useState("");
    const [activeUsers, setActiveUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const eventId = history.location.pathname.split('/').pop();
                const event = await AuthApi.editRole(eventId);

                if (event.status && event.data) {
                    const roleName = event.data.role_name || "";
                    const eventPermissions = event.data.permissions || {};
                    const tmsUserIds = Array.isArray(event.data.tms_user_ids)
                        ? event.data.tms_user_ids.map(Number)
                        : [];
                    const initialPermissions = [];

                    Object.entries(eventPermissions).forEach(([key, permArray]) => {
                        permArray.forEach(perm => {
                            if (perm.checked) {
                                initialPermissions.push(perm.id);
                            }
                        });
                    });

                    setEditValues({
                        roleName,
                        permissions: initialPermissions,
                        event_Id: eventId,
                        tms_user_ids: tmsUserIds,
                    });
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        const fetchActiveUsers = async () => {
            try {
                setUsersLoading(true);
                const res = await AuthApi.getData();
                const rawUsers = (res && res.data) || res || [];
                if (Array.isArray(rawUsers)) {
                    const active = rawUsers.filter(u => Number(u.user_logged) === 1 && !u.deactivated_at);
                    active.sort((a, b) => {
                        const nameA = (`${a.firstName || ''} ${a.lastName || ''}`.trim() || a.name || '').toLowerCase();
                        const nameB = (`${b.firstName || ''} ${b.lastName || ''}`.trim() || b.name || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                    });
                    setActiveUsers(active);
                }
            } catch (err) {
                console.error("Error fetching active users:", err);
            } finally {
                setUsersLoading(false);
            }
        };

        fetchData();
        getPermissions();
        fetchActiveUsers();
    }, [history.location.pathname]);

    const getPermissions = async () => {
        swal({
            title: "Loading",
            text: "Please wait...",
            buttons: false,
            closeOnClickOutside: false,
            closeOnEsc: false,
            width: "10%",
            content: createLoaderContent()
        });
        try {
            const permissionResponse = await AuthApi.getpermissions();
            swal.close();
            if (permissionResponse.status) {
                setPermissions(permissionResponse.permissions);
            }
        } catch (error) {
            swal.close();
            console.error("Error fetching permissions:", error);
        }
    };

    const handleCheckboxChange = (event) => {
        const { name, checked } = event.target;
        const permissionId = parseInt(name, 10);
        setEditValues(prevState => {
            const newPermissions = checked
                ? [...prevState.permissions, permissionId]
                : prevState.permissions.filter(permission => permission !== permissionId);
            return { ...prevState, permissions: newPermissions };
        });
    };

    const handleParentCheckboxChange = (parent, children, checked) => {
        setEditValues(prevState => {
            const childIds = children.map(child => child.id);
            const newPermissions = checked
                ? [...new Set([...prevState.permissions, ...childIds])]
                : prevState.permissions.filter(permission => !childIds.includes(permission));
            return { ...prevState, permissions: newPermissions };
        });
    };

    const isParentChecked = (children) => {
        return children.every(child => editValues.permissions.includes(child.id));
    };

    const getParentDisplayName = (parent) => {
        if (parent === "Tasks" || parent === "TaskManagement") {
            return "Task Management";
        }
        return parent;
    };

    const isTaskManagementParent = (parent) => {
        return parent === "Tasks" || parent === "TaskManagement" || getParentDisplayName(parent) === "Task Management";
    };

    const getFilteredPermissions = () => {
        const normalizedQuery = permissionSearch.trim().toLowerCase();

        if (!normalizedQuery) {
            return Object.entries(permissions);
        }

        return Object.entries(permissions)
            .map(([parent, children]) => {
                const displayName = getParentDisplayName(parent);
                const parentMatches =
                    parent.toLowerCase().includes(normalizedQuery) ||
                    displayName.toLowerCase().includes(normalizedQuery) ||
                    (parent === "Tasks" && "task management tms".includes(normalizedQuery));
                const filteredChildren = parentMatches
                    ? children
                    : children.filter((permission) =>
                        permission.value.toLowerCase().includes(normalizedQuery)
                    );

                return [parent, filteredChildren];
            })
            .filter(([, children]) => children.length > 0);
    };

    const hardik = editValues.permissions;
    const filteredPermissions = getFilteredPermissions();

    const handleSubmit = async (event) => {
        event.preventDefault();
        swal({
            title: "Loading",
            text: "Please wait...",
            buttons: false,
            closeOnClickOutside: false,
            closeOnEsc: false,
            width: "10%",
            content: createLoaderContent()
        });
        try {
            const hasAnyTaskPermission = Object.entries(permissions).some(([parent, children]) =>
                isTaskManagementParent(parent) &&
                children.some(c => hardik.includes(c.id))
            );
            const tmsUserIdsToSave = hasAnyTaskPermission ? (editValues.tms_user_ids || []) : [];

            const response = await AuthApi.updateRole(
                editValues.event_Id,
                hardik,
                editValues.roleName,
                tmsUserIdsToSave
            );
            swal.close();
            if (response.status) {
                swal("Success", "Role updated successfully!", "success");
                history.push('/role');
            } else {
                console.error("Error updating role:", response.message);
                swal("Error", response.message || "Failed to update role.", "error");
            }
        } catch (error) {
            swal.close();
            console.error("Error updating role:", error);
            swal("Error", "An error occurred while updating the role.", "error");
        }
    };

    return (
        <div>
            <AuthCheckComponent authToken={props.authToken} />
            <Header
                {...props}
                authUser={props.authUser}
                component={
                    <div>
                        <div className="holiday-breadcrumb-card">
                            <Breadcrumb
                                {...props}
                                primaryPageName="Role"
                                primaryPageLink="/role"
                                isSecondaryPage={true}
                                secondaryPageName="Permission"
                            />
                        </div>
                        <div className="holiday-form-card">
                            <Typography variant="h2" className="common-form-page-title holiday-form-title">Edit Role</Typography>
                            <form onSubmit={handleSubmit} className="common-form-fields">
                                <Grid
                                    container
                                    spacing={3}
                                    style={{ marginTop: "10px", alignItems: "center", justifyContent: "space-between" }}
                                >
                                    <Grid item xs={12} md={5}>
                                        <TextField
                                            fullWidth
                                            id="roleName"
                                            name="roleName"
                                            label="Role Name"
                                            variant="outlined"
                                            value={editValues.roleName}
                                            onChange={(e) => setEditValues({ ...editValues, roleName: e.target.value })}
                                            className="holiday-form-input"
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={8} md={3} style={{ marginLeft: "auto" }}>
                                        <div className="holiday-search-wrapper" style={{ width: "100%" }}>
                                          <TextField
                                            fullWidth
                                            className="holiday-search-field"
                                            label="Search"
                                            value={permissionSearch}
                                            onChange={(e) => setPermissionSearch(e.target.value)}
                                            variant="outlined"
                                            InputLabelProps={{
                                              shrink: permissionSearch !== "",
                                              style: {
                                                transition: 'all 0.2s',
                                                transform: permissionSearch !== "" ? 'translate(14px, -6px) scale(0.75)' : 'translate(14px, 11px) scale(1)',
                                                pointerEvents: 'none'
                                              }
                                            }}
                                            InputProps={{
                                              endAdornment: (
                                                <InputAdornment position="end" style={{
                                                  padding: "0 5px",
                                                  minWidth: "40px",
                                                  justifyContent: "center",
                                                  borderLeft: "1px solid rgba(0, 0, 0, 0.23)",
                                                  backgroundColor: "transparent",
                                                  height: "100%",
                                                  maxHeight: "none",
                                                  margin: "0",
                                                }}>
                                                  {permissionSearch && (
                                                    <ClearIcon onClick={() => setPermissionSearch("")} style={{ cursor: 'pointer' }} />
                                                  )}
                                                  <SearchIcon style={{ cursor: 'pointer' }} />
                                                </InputAdornment>
                                              ),
                                              style: { paddingRight: 0, height: "40px", padding: 0 }
                                            }}
                                          />
                                        </div>
                                    </Grid>

                                    <Grid container
                                        style={{
                                            MozColumnCount: 3,
                                            MozColumnGap: '20px',
                                            WebkitColumnCount: 3,
                                            WebkitColumnGap: '20px',
                                            columnCount: 3,
                                            columnGap: '20px',
                                            display: 'block',
                                            marginTop: "35px"
                                        }}
                                    >
                                        {filteredPermissions.map(([parent, children], parentIndex) => {
                                            const isTaskManagement = isTaskManagementParent(parent);
                                            const isTaskChecked = isParentChecked(children) || children.some(c => editValues.permissions.includes(c.id));

                                            return (
                                                <TreeView
                                                    key={parent}
                                                    className="role-permission-tree"
                                                    defaultCollapseIcon={<ExpandMoreIcon />}
                                                    defaultExpandIcon={<ChevronRightIcon />}
                                                >
                                                    <TreeItem
                                                        nodeId={parent}
                                                        className={isTaskManagement ? "tms-role-tree-item" : ""}
                                                        classes={{
                                                            content: isTaskManagement ? classes.tmsTreeItemContent : undefined,
                                                            iconContainer: isTaskManagement ? classes.tmsTreeItemIcon : undefined,
                                                        }}
                                                        label={
                                                            <div style={{ width: '100%' }}>
                                                                <FormControlLabel
                                                                    className={classes.treeItem}
                                                                    control={
                                                                        <Checkbox
                                                                            checked={isParentChecked(children)}
                                                                            onChange={(e) => handleParentCheckboxChange(parent, children, e.target.checked)}
                                                                            color="primary"
                                                                        />
                                                                    }
                                                                    label={getParentDisplayName(parent)}
                                                                />
                                                                {isTaskManagement && isTaskChecked && (
                                                                    <TmsUserSelect
                                                                        selectedUserIds={editValues.tms_user_ids || []}
                                                                        activeUsers={activeUsers}
                                                                        loading={usersLoading}
                                                                        onChange={(newIds) => {
                                                                            setEditValues(prev => ({
                                                                                ...prev,
                                                                                tms_user_ids: newIds
                                                                            }));
                                                                        }}
                                                                    />
                                                                )}
                                                            </div>
                                                        }
                                                    >
                                                        {children.map((permission, childIndex) => (
                                                            <div key={childIndex} className={classes.treeItem}
                                                                style={{ marginLeft: "24px", marginTop: "-11px" }}
                                                            >
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            checked={editValues.permissions.includes(permission.id)}
                                                                            onChange={handleCheckboxChange}
                                                                            name={permission.id.toString()}
                                                                            color="primary"
                                                                        />
                                                                    }
                                                                    label={permission.value}
                                                                />
                                                            </div>
                                                        ))}
                                                    </TreeItem>
                                                </TreeView>
                                            );
                                        })}
                                        {filteredPermissions.length === 0 && (
                                            <Typography
                                                variant="body2"
                                                style={{ color: '#6b7280', padding: '4px 0 0 4px' }}
                                            >
                                                No permissions found.
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>

                                <div className="holiday-form-actions">
                                    <Button type="submit" className="btn-success" variant="contained" color="primary" style={{ textTransform: 'none' }}>Edit</Button>
                                    <Button type="button" className="btn-cancel" onClick={() => history.push('/role')} style={{ textTransform: 'none' }}>Cancel</Button>
                                </div>
                            </form>
                        </div>
                    </div>
                }
            />
            <Footer />
        </div>
    );
};

export default EditRole;
